import clsx from 'clsx';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactElasticCarousel, { RenderArrowProps, RenderPaginationProps } from 'react-elastic-carousel';

import { WidgetItemAspectRatioDefault, WidgetSingleRowLayoutConfiguration, WidgetSingleRowLayoutVisibleItems } from '@vestico/model';

import { CustomCSSProperties, GlobalsContext, useElementSize } from '@vestico/shared';

import { getIsRTL } from '@vestico/i18n';

import { CarouselItem } from '../common/CarouselItem';
import { useCarouselStyles } from '../hooks/useCarouselStyles';
import { useInViewVideo } from '../hooks/useInViewVideo';
import { usePreviewTexts } from '../hooks/usePreviewTexts';
import { BaseLayoutProps } from '../model/baseLayoutProps';
import { LayoutLoadMoreHandler } from './LayoutLoadMoreHandler';
import style from './RowLayout.module.css';
import { RowLayoutArrow } from './RowLayoutArrow';
import { RowLayoutPagination } from './RowLayoutPagination';

const CAROUSEL_OUTER_SPACING = 30;

export const getItemsToShow = (
  visibleItems: WidgetSingleRowLayoutVisibleItems | undefined,
  mainContainerWidth: number,
  numberOfResponses: number
) => {
  let visibleItemsCount = 2;

  switch (visibleItems) {
    case WidgetSingleRowLayoutVisibleItems.dynamic:
    case undefined:
      if (mainContainerWidth < 600) {
        visibleItemsCount = 2;
      } else if (mainContainerWidth < 780) {
        visibleItemsCount = 3;
      } else {
        visibleItemsCount = 5;
      }
      visibleItemsCount = Math.max(Math.min(visibleItemsCount, numberOfResponses), 1);
      break;
    case WidgetSingleRowLayoutVisibleItems.dynamicLarge:
      if (mainContainerWidth < 600) {
        visibleItemsCount = 1;
      } else if (mainContainerWidth < 780) {
        visibleItemsCount = 2;
      } else if (mainContainerWidth < 1200) {
        visibleItemsCount = 3;
      } else {
        visibleItemsCount = 5;
      }
      visibleItemsCount = Math.max(Math.min(visibleItemsCount, numberOfResponses), 1);
      break;
    case WidgetSingleRowLayoutVisibleItems.one:
    case WidgetSingleRowLayoutVisibleItems.two:
    case WidgetSingleRowLayoutVisibleItems.three:
    case WidgetSingleRowLayoutVisibleItems.four:
    case WidgetSingleRowLayoutVisibleItems.five:
    case WidgetSingleRowLayoutVisibleItems.six:
    case WidgetSingleRowLayoutVisibleItems.seven:
      visibleItemsCount = parseInt(visibleItems, 10);
      break;
    default:
      throw new Error(`Unhandled visible items ${visibleItems}`);
  }
  return visibleItemsCount;
};

export interface RowLayoutProps extends BaseLayoutProps {
  layout: WidgetSingleRowLayoutConfiguration;
}

export const RowLayout = ({ layout, responses, onResponseClick, loading, loadMore, hasMore }: RowLayoutProps) => {
  const { visibleItems, showLikes, itemBorderRadius } = layout;
  const aspectRatio = useMemo(() => layout.aspectRatio ?? WidgetItemAspectRatioDefault, [layout.aspectRatio]);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const itemPadding = useMemo(() => layout.itemPadding ?? 12, [layout.itemPadding]);

  const { useMobileLayout, isTouchDevice } = useContext(GlobalsContext);

  const [itemSize, setItemSize] = useState<number | undefined>(undefined);
  const [itemsToShow, setItemsToShow] = useState<number>(2);
  const [outerSpacing, setOuterSpacing] = useState(0);

  const mainContainerSize = useElementSize(mainContainerRef);

  useEffect(() => {
    const calculateItemsToShow = () => {
      const mainContainerWidth = mainContainerSize.width;
      // Figure out how many items to show
      const visibleItemsCount = getItemsToShow(visibleItems, mainContainerWidth, responses.length);

      // Double the padding when we're in 1up mode
      const dynamicItemPadding = (visibleItemsCount === 1 ? 2 : 1) * (itemPadding ?? 0);
      const maxWidth = visibleItemsCount === 1 ? 520 : 360;

      // We show next/previous posts as 'hints' when there's only one or when we're on desktop
      const newShowNextPreviousReponse = (visibleItemsCount === 1 || !useMobileLayout) && responses.length > visibleItemsCount;
      const newOuterSpacing = !newShowNextPreviousReponse
        ? (dynamicItemPadding * -0.5) / visibleItemsCount
        : CAROUSEL_OUTER_SPACING + dynamicItemPadding;
      const numberOfPaddings = !newShowNextPreviousReponse ? visibleItemsCount - 1 : visibleItemsCount;
      const itemWidth = Math.min((mainContainerWidth - numberOfPaddings * dynamicItemPadding - newOuterSpacing * 2.0) / visibleItemsCount, maxWidth);

      setItemsToShow(visibleItemsCount);
      setItemSize(itemWidth);
      setOuterSpacing(newOuterSpacing);
    };

    calculateItemsToShow();
  }, [useMobileLayout, mainContainerSize, mainContainerRef, responses, itemPadding, visibleItems]);

  const emptyChildren = useMemo(() => {
    if (responses.length === 0) {
      return undefined;
    }

    const children = Array(responses.length).fill(<div />);
    return children;
  }, [responses]);

  const [currentCarouselIdx, setCurrentCarouselIdx] = useState(0);
  const responsePreviewTexts = usePreviewTexts(layout, responses);

  const handleResponseClick = useCallback((index: number) => onResponseClick(index), [onResponseClick]);
  const { firstInViewVideoResponse, updateInView } = useInViewVideo({ layout, responses });
  const onUpdateInView = useCallback((index: number) => (inView: boolean) => updateInView(index, inView), [updateInView]);

  const customCSS = useMemo(() => {
    const stylesOverrides: CustomCSSProperties = {};
    stylesOverrides['--vw-inline-row-carousel-margin'] = `${outerSpacing}px`;
    stylesOverrides['--vw-inline-row-carousel-arrow-width'] = `${CAROUSEL_OUTER_SPACING + (itemPadding ?? 0) * 0.5}px`;

    return stylesOverrides;
  }, [itemPadding, outerSpacing]);
  const { styles, borderRadiusStyles } = useCarouselStyles({ aspectRatio, borderRadius: itemBorderRadius, itemSize, customCSS });

  const responseMarkups = useMemo(() => {
    if (responses.length === 0 || !emptyChildren) {
      return [];
    }

    const children = [...emptyChildren];

    const absoluteCarouselIdx = currentCarouselIdx * itemsToShow;
    const startIdx = 0;
    const endIdx = Math.min(absoluteCarouselIdx + itemsToShow + 1, children.length - 1);

    const finalChildren = children.map((emptyChild, index) => {
      if (index >= startIdx && index <= endIdx) {
        const currentResponse = responses[index];
        return (
          <CarouselItem
            key={currentResponse.postDocumentID}
            index={index}
            response={currentResponse}
            onClick={handleResponseClick}
            previewText={responsePreviewTexts[index]}
            showLikes={showLikes}
            aspectRatio={aspectRatio}
            updateInView={onUpdateInView(index)}
            playVideo={currentResponse === firstInViewVideoResponse}
            borderRadius={borderRadiusStyles}
          />
        );
      }
      return emptyChild;
    });

    const loadMoreHandler = hasMore ? <LayoutLoadMoreHandler loading={loading} loadMore={loadMore} isItem /> : null;
    return [...finalChildren, loadMoreHandler];
  }, [
    responses,
    emptyChildren,
    currentCarouselIdx,
    itemsToShow,
    hasMore,
    loading,
    loadMore,
    handleResponseClick,
    responsePreviewTexts,
    showLikes,
    aspectRatio,
    onUpdateInView,
    firstInViewVideoResponse,
    borderRadiusStyles,
  ]);

  // Only show pagination on mobile when there's mulitple pages and we're not in one-up mode
  const showPagination = useMobileLayout && responseMarkups !== null && responseMarkups.length > itemsToShow && itemsToShow > 1;

  const isRTL = getIsRTL();

  const onChange = useCallback((obj: unknown, index: number) => setCurrentCarouselIdx(index), []);

  const renderArrow = useCallback(
    ({ type, onClick, isEdge }: RenderArrowProps) => (
      <RowLayoutArrow type={type} isRTL={isRTL} onClick={onClick} hidden={useMobileLayout || isEdge} />
    ),
    [isRTL, useMobileLayout]
  );

  const renderPagination = useCallback(
    ({ pages, activePage, onClick }: RenderPaginationProps) => (
      <RowLayoutPagination pages={pages} activePage={activePage} onClick={isTouchDevice ? undefined : onClick} />
    ),
    [isTouchDevice]
  );

  const carousel =
    itemSize === undefined ? null : (
      // @ts-expect-error - 2322
      <ReactElasticCarousel
        itemsToShow={itemsToShow}
        itemsToScroll={showPagination ? itemsToShow : 1}
        outerSpacing={outerSpacing}
        isRTL={isRTL}
        pagination={showPagination}
        transitionMs={300}
        initialActiveIndex={0}
        onChange={onChange}
        renderArrow={renderArrow}
        renderPagination={renderPagination}
        preventDefaultTouchmoveEvent
      >
        {responseMarkups}
      </ReactElasticCarousel>
    );

  return (
    <div
      className={clsx(style.root, outerSpacing !== 0 ? 'vestico-inline-row-carousel-with-outer-spacing' : '')}
      style={styles}
      ref={mainContainerRef}
    >
      {carousel}
    </div>
  );
};
